import React, { useState, useEffect } from 'react';
import {
  BaseModal,
  Button,
  Input,
  Switch,
  Multiselect,
  ToastMessage
} from '@cokitchen/cokitchen-components';
import { ReactComponent as PackagingIcon } from 'assets/icons/packaging-icon.svg';
import { EditAltIcon } from 'cokitchen-icons';
import { toast } from 'react-toastify';
import { apiService } from 'utils/api';
import { PackagingScreenType } from 'models/packaging-screen.model';
import { BrandType } from 'models/brand.model';
import { STORAGE_KEYS } from 'utils/constants';
import { handleApiError } from 'utils/functions';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  type: 'edit' | 'create';
  refreshData?: () => void;
  screenData: PackagingScreenType | null;
  onEditSuccess?: (arg0: FormDataType) => void;
};

export type FormDataType = {
  name: string;
  active: boolean;
  id?: string;
  brand_ids: string[];
};

const CreatePackagingScreenModal = ({
  isOpen,
  onClose,
  type,
  refreshData,
  screenData,
  onEditSuccess
}: Props) => {
  const cokitchen_id = localStorage.getItem(STORAGE_KEYS.COKITCHEN);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormDataType>({
    name: screenData?.name ?? '',
    active: screenData?.active ?? true,
    id: screenData?.id,
    brand_ids: screenData?.brand_ids ?? []
  });
  const [brandOptions, setBrandOptions] = useState<BrandType[]>([]);
  const [brandIds, setBrandIds] = useState<string[]>(formData.brand_ids ?? []);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const res = await apiService.get<{ brands: BrandType[] }>('brands/kitchen', {
          baseUrl: process.env.REACT_APP_KITCHEN_API_URL
        });
        const cokitchenBrands =
          res.data.brands?.filter((b) => b.cokitchen_id === cokitchen_id) ?? [];
        setBrandOptions(cokitchenBrands);
      } catch (err) {
        handleApiError(err);
      }
    };

    fetchBrands();
  }, []);

  const createPackagingScreen = async () => {
    setLoading(true);
    const data = { ...formData, cokitchen_id, brand_ids: brandIds };
    try {
      await apiService.post('kitchen/packaging-screens', {
        data,
        baseUrl: process.env.REACT_APP_KITCHEN_API_URL
      });
      onClose();
      refreshData?.();
      toast(<ToastMessage type='success' message='Packaging Screen created successfully' />);
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    } finally {
      setLoading(false);
    }
  };

  const EditPackagingScreen = async () => {
    setLoading(true);
    const data = {
      name: formData.name,
      active: formData.active,
      cokitchen_id,
      brand_ids: brandIds
    };
    try {
      await apiService.put(`kitchen/packaging-screens/${formData?.id}`, {
        data,
        baseUrl: process.env.REACT_APP_KITCHEN_API_URL
      });
      const editedScreen: any = { ...data, id: formData?.id };
      onEditSuccess?.(editedScreen);
      onClose();
      toast(<ToastMessage type='success' message='Packaging Screen updated successfully' />);
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    } finally {
      setLoading(false);
    }
  };

  const onConfirm = async () => {
    if (type === 'create') {
      await createPackagingScreen();
    } else {
      await EditPackagingScreen();
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onRequestClose={onClose}
      title={type === 'create' ? 'Create Screen' : 'Edit Screen'}
      titleIcon={type === 'create' ? <PackagingIcon /> : <EditAltIcon color='#84cbf5' />}
      titleIconColor='info'
      footer={
        <div className='flex gap-4'>
          <Button color='primary' variant='outline' small onClick={onClose}>
            Cancel
          </Button>
          <Button
            color='primary'
            variant='block'
            small
            disabled={!formData.name}
            loading={loading}
            onClick={onConfirm}>
            Confirm
          </Button>
        </div>
      }>
      <div>
        <Input
          label='Name of screen'
          type='text'
          placeholder='1'
          className='text-[#A3A3A3]'
          value={formData?.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
        <div className='w-full mb-8 text-black mt-6'>
          <p className='text-sm font-medium mb-[6px]'>
            Brands selection (Leave empty to apply to all brands)
          </p>
          <Multiselect
            options={brandOptions.map((brand) => ({
              value: brand.id,
              text: brand.name
            }))}
            values={brandIds}
            onChange={setBrandIds}
            placeholder='Select brand'
            dropdownPlaceholder='Select all or multiple brands'
          />
        </div>

        <div className='bg-[#F9F6F6] py-2 px-3 h-[47px] rounded-md border border-[#EEECEC] my-6'>
          <Switch
            active={formData.active}
            onChange={(e) => setFormData({ ...formData, active: e })}
            label='Active'
            small
            labelPosition='right'
          />
        </div>
      </div>
    </BaseModal>
  );
};

export default CreatePackagingScreenModal;
